/**
 * Remembered open/closed state for collapsible panels.
 *
 * Split out from the component because this is the part that fails quietly:
 * localStorage throws in private-mode Safari and inside sandboxed frames, and
 * a panel that cannot remember a preference must still render. Everything here
 * degrades to "no preference recorded" rather than propagating.
 */

const STORAGE_PREFIX = 'studio_section_';

/**
 * The host's recorded choice, or null when they have never made one.
 *
 * The null is load-bearing: it lets a caller distinguish "the host closed this"
 * from "the host has not touched it", so a default can still change with the
 * data without overriding a deliberate choice.
 */
export function readSectionOpen(id: string): boolean | null {
  try {
    const stored = globalThis.localStorage?.getItem(`${STORAGE_PREFIX}${id}`);
    if (stored === 'open') return true;
    if (stored === 'closed') return false;
    // Anything else — absent, or a value written by an older build — counts as
    // no preference rather than as "closed", which would silently fold panels
    // a host never chose to fold.
    return null;
  } catch {
    return null;
  }
}

export function writeSectionOpen(id: string, open: boolean): void {
  try {
    globalThis.localStorage?.setItem(`${STORAGE_PREFIX}${id}`, open ? 'open' : 'closed');
  } catch {
    /* A lost preference changes nothing that matters. */
  }
}
